import type { FC } from 'react';
import React, { memo, useRef, useContext, useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { UFOTransitionMarkFMP } from '@confluence/browser-metrics/entry-points/UFOTransitionMarkFMP.ts';
import {
	VIEW_PUBLIC_LINK_SHARED_PAGE_EXPERIENCE,
	VIEW_CONTENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { isInitial } from '@confluence/browser-metrics/entry-points/utils.ts';
import { macrosPreloadPerformed } from '@confluence/fabric-extension-lib/entry-points/macrosPreloadPerformed.ts';
import { getPreloadFailedMacros } from '@confluence/fabric-extension-lib/entry-points/getPreloadFailedMacros.ts';
import { usePageState } from '@confluence/page-context';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import { CONTENT_RENDERER_METRIC } from './perf.config.ts';

const contentViews = new Set<string>();

type PerformanceMetricsComponentProps = {
	revision?: string;
	appearance: string;
	contentId: string;
	fabric: boolean;
	loadedFromCache: boolean;
	isAutoConverted?: boolean;
	customPerfData?: Record<string, string | boolean | object>;
};
export const usePrevious = (value: any): undefined => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

const createRendererMeasure = () => {
	try {
		performance.mark('rendererRendered.end');
		performance.measure(
			'browser-metric:ContentRenderer',
			isInitial() ? undefined : 'browser-metric.start',
			'rendererRendered.end',
		);
		// eslint-disable-next-line no-unused-vars
	} catch (e) {
		// Suppress error when performance API failed.
		// Note: in MicrosoftEdge version 18 and prior, performance.measure(name, startMark, endMark) API will throw SyntaxError if startMark is undefined.
		// That's why wrap with try catch statement.
	}
};

const augmentWithNewTTI = (customData) => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR || !isInitial()) {
		return customData;
	}

	// Since TTI is not yet available at this point, use rendererRendered as the closest
	// approximation, and take actual measurement of FMP. Here FMP is defined and available
	// when page is successfully SSR'ed. Otherwise FMP *will be* evaluated as TTI later on.
	const tti = performance.getEntriesByName('rendererRendered.end').pop()?.startTime || 0;
	const fmp = performance.getEntriesByName('browser-metric:fmp').pop()?.duration || 0;

	const macroFailures = getPreloadFailedMacros();
	// nTTI is considered equal to FMP in the following cases:
	//  - page is successfully server-rendered;
	//  - fmp is already known at the given point;
	//  - all requested macros preloaded before preloading is cut off by timeout.
	// In all other cases nTTI = TTI.
	let ntti = tti;
	if (window.__SSR_RENDERED__ && !macroFailures && fmp > 0) {
		ntti = fmp;
	}

	return {
		...customData,
		macroFailures,
		// Both `fmp` and `tti` metrics are rounded to 1ms by BM library
		nTTI: Math.round(ntti),
	};
};

const PerformanceMetricsComponent: FC<PerformanceMetricsComponentProps> = ({
	revision,
	appearance,
	contentId,
	fabric,
	loadedFromCache,
	isAutoConverted,
	customPerfData,
}) => {
	const [{ isLite, routeName }] = usePageState();
	// Temp query param check if ccProxyTarget logic is still being used - CCP-2348
	const { getQueryParams } = useContext(RoutesContext);
	const [rendererMeasureCreated, setRendererMeasureCreated] = useState(false);
	const { ccProxyTarget, ccProxyStaticApp, homepageId } = getQueryParams();

	const hasHomepageId = Boolean(routeName === 'SPACE_OVERVIEW' && homepageId);

	const isPublicLink = routeName === 'EXTERNAL_SHARE';
	const experienceName = isPublicLink
		? VIEW_PUBLIC_LINK_SHARED_PAGE_EXPERIENCE
		: VIEW_CONTENT_EXPERIENCE;

	const macroFailures = getPreloadFailedMacros();
	const endMarkWhenMacrosSSRed =
		macrosPreloadPerformed() && !macroFailures ? getMark('CFP-63.ssr-ttr') : undefined;

	const createRendererMeasureOnComplete = useCallback(() => {
		createRendererMeasure();
		setRendererMeasureCreated(true);
	}, [setRendererMeasureCreated]);

	const customData = {
		appearance,
		contentId,
		fabric,
		isLite: Boolean(isLite),
		ccProxyTarget: Boolean(ccProxyTarget || ccProxyStaticApp),
		loadedFromCache,
		isAutoConverted,
		hasHomepageId,
		...customPerfData,
	};

	return (
		<>
			<UFOLoadHold name="CreateRendererMeasure" hold={!rendererMeasureCreated} />
			<UFOTransitionMarkFMP />
			<ExperienceSuccess
				key={`es${contentId}_${revision}`}
				name={experienceName}
				attributes={{ fabric }}
			/>
			<PageSegmentLoadEnd
				key={`psle${contentId}`}
				metric={CONTENT_RENDERER_METRIC}
				customData={customData}
				stopTime={endMarkWhenMacrosSSRed}
				onComplete={createRendererMeasureOnComplete}
				augmentCustomDataAfterComplete={augmentWithNewTTI}
			/>
		</>
	);
};

type PerformanceMetricsProps = {
	appearance: string;
	contentId: string;
	fabric: boolean;
	revision?: string;
	isAutoConverted?: boolean;
	customPerfData?: Record<string, string | boolean | object>;
};

// The `PerformanceMetrics` component is memoized because performance and
// experience tracking components used below rely on their side effects to emit
// analytics and multiple re-rendering of these components may alter the timing
// of events being sent, and even data being sent.
// The memo ensures that these tracking components are rendered once per given
// set of props, which will make perf/experience tracking accurate.
export const PerformanceMetrics: React.MemoExoticComponent<
	({
		appearance,
		fabric,
		contentId,
		revision,
		isAutoConverted,
		customPerfData,
	}: PerformanceMetricsProps) => React.JSX.Element | null
> = memo(
	({
		appearance,
		fabric,
		contentId,
		revision,
		isAutoConverted,
		customPerfData,
	}: PerformanceMetricsProps): React.JSX.Element | null => {
		// This is a heuristic allowing to tell more or less accurately whether page has been loaded
		// from cache or from network. Heuristic is based on assumption that if any given contentId
		// was visited the corresponding content will be cached.
		// The only exception to that heuristic is transition from editor to renderer, which can be
		// identified and filtered out when querying data.
		// WARN: this is temp solution to figure out how much benefit our customers get from cached
		// pages content.
		const loadedFromCacheRef = useRef(contentViews.has(contentId));
		const loadedFromCache = loadedFromCacheRef.current;

		useEffect(() => {
			contentViews.add(contentId);
		}, [contentId]);

		// Only gather metrics if the content revision has changed since the last render.
		// This tracks transitions more accurately and also prevents metrics miss-firing
		// if the PageContentRenderer component re-renders for any reason.
		const prevRevision = usePrevious(revision) ?? null;
		const revisionChanged = revision !== prevRevision;

		if (revisionChanged) {
			return (
				<>
					<PerformanceMetricsComponent
						revision={revision}
						appearance={appearance}
						contentId={contentId}
						isAutoConverted={isAutoConverted}
						fabric={fabric}
						loadedFromCache={loadedFromCache}
						customPerfData={customPerfData}
					/>
				</>
			);
		} else {
			return null;
		}
	},
	isEqual,
);
